import { useCallback, useEffect, useMemo } from 'react';

import { usePosDataQuery } from 'generated/sanity-graphql';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { getRestaurantsByStoreNumber } from 'remote/api/restaurants';
import { useLocationContext } from 'state/location';
import { useLoggerContext } from 'state/logger';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';
import { isKiosk } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { transformPlus } from 'utils/menu';
import { parseStringifiedJSON } from 'utils/parse-string';
import { routes } from 'utils/routing';

import getRestaurantPosDataPlus from './get-restaurant-pos-data-plus';
import { IStoreContext, Store, StoreState } from './types';
import useRefreshStore from './use-refresh-store';

export const resetLastTimeStoreUpdated: IStoreContext['resetLastTimeStoreUpdated'] = currentTime =>
  LocalStorage.setItem(StorageKeys.LAST_TIME_STORE_UPDATED, currentTime || Date.now());

export const useStoreUtils = ({
  resetStore,
  store
}: {
  resetStore: IStoreContext['resetStore'];
  /** IStore is the store type for store selection 2.0 {@link:docs/design-reviews/store-selection-2.0/README.md} */
  store: StoreState | Store;
}) => {
  const { location, navigate } = useLocationContext();
  const { decorateLogger, logger } = useLoggerContext();
  const mParticle = useMParticleContext();
  const { connection, sanityEndpoints } = useNetworkContext();
  const { serviceMode } = useServiceModeContext();

  const menuPullLogAttrs: { StoreId?: string } = {};
  let storePosDataId: string | null | undefined;
  let storeEmail = '';

  /** set up values based on type of store */
  if (store === null) {
    menuPullLogAttrs.StoreId = undefined;
    storePosDataId = null;
  } else if ('number' in store) {
    /** store is of type {@link:SelectedStore} or {@link:IStore} */
    menuPullLogAttrs.StoreId = store.number;
    storePosDataId = store.restaurantPosData?._id;
    storeEmail = store?.email?.[0] ?? '';
  } else if ('selectedUnavailableStoreNumber' in store) {
    /** store is of type {@link:SelectedUnavailableStore} */
    menuPullLogAttrs.StoreId = store.selectedUnavailableStoreNumber ?? undefined;
    storePosDataId = null;
  }

  const {
    serviceModeStatus,
    isRestaurantAvailable,
    checkIfRestaurantPosIsOnline
  } = useServiceModeStatus(store);

  const { data: posData, refetch: getPosData } = usePosDataQuery({
    fetchPolicy: isKiosk ? 'network-only' : 'cache-and-network',
    onError: () => {
      if (isKiosk) {
        mParticle.logEvent(
          CustomEventNames.KIOSK_MENU_PULL_ERROR,
          EventTypes.Other,
          menuPullLogAttrs
        );
      }
    },
    onCompleted: () => {
      if (isKiosk) {
        mParticle.logEvent(
          CustomEventNames.KIOSK_MENU_PULL_SUCCESS,
          EventTypes.Other,
          menuPullLogAttrs
        );
      }
    },
    skip: !storePosDataId,
    variables: {
      id: storePosDataId || ''
    }
  });

  const disableChickenSandwich = useMemo(
    () => Boolean(posData?.RestaurantPosData?.disableChickenSandwich),
    [posData]
  );

  const onConfirmLocateRestaurants = useCallback(() => {
    resetStore();

    if (location.pathname !== routes.storeLocator) {
      navigate(routes.storeLocator);
    }
  }, [location.pathname, navigate, resetStore]);

  const fetchStore = useCallback(
    async (storeId: string) => {
      const isRestaurantPosAvailable = await checkIfRestaurantPosIsOnline(storeId);

      if (!isKiosk && !isRestaurantPosAvailable) {
        onConfirmLocateRestaurants();
        return;
      }

      const [updatedStoreData] = await getRestaurantsByStoreNumber(sanityEndpoints.groq, {
        connection,
        storeNumber: storeId
      });

      return updatedStoreData;
    },
    [checkIfRestaurantPosIsOnline, connection, onConfirmLocateRestaurants, sanityEndpoints.groq]
  );

  const prices: Record<string, number> = useMemo(() => {
    const rawPlus = getRestaurantPosDataPlus(posData, serviceMode);

    if (!rawPlus) {
      return {};
    }

    return transformPlus(parseStringifiedJSON({ defaultValue: {}, value: rawPlus }));
  }, [posData, serviceMode]);

  useEffect(() => {
    let storeId = 'No store selected';

    if (store !== null && 'number' in store) {
      /** store is of type {@link:SelectedStore} or {@link:IStore} */
      storeId = store.number;
    }

    decorateLogger({
      storeId
    });

    if (store === null || ('hasSelection' in store && !store.hasSelection)) {
      /** store is of type {@link:SelectedUnavailableStore} or null */
      return;
    }

    if (!store.restaurantPosData) {
      return logger.error({ message: 'No POS data for selected store', storeId: store._id });
    }
  }, [store]); // eslint-disable-line react-hooks/exhaustive-deps

  useRefreshStore({
    getRestaurantPosData: (storeId: string) => {
      getPosData({
        id: storeId
      });
    },
    store
  });

  return {
    checkIfRestaurantPosIsOnline,
    disableChickenSandwich,
    email: storeEmail,
    fetchStore,
    isRestaurantAvailable,
    prices,
    resetLastTimeStoreUpdated,
    serviceModeStatus
  };
};
